import { cloneDeep, isEmpty, omit } from 'lodash';
import React, { useEffect, useState } from 'react';
import { LuAward, LuX } from 'react-icons/lu';
import { toast } from 'sonner';

import { Account, AccountConvictionsSaveRequest, Conviction, ConvictionType } from '@ndvr/portfolio-api';

import { AssetInfo } from '@/cip/_openapi/portfolio-optimizer';
import { EditableCard } from '@/components/EditableCard';
import { useEditableCardGroup } from '@/components/EditableCardGroupContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useRequiredParams } from '@/utils';
import { useUpdateAccountConvictions } from '@/views/portfolio/account-group-queries';
import { ExtendedConviction } from 'views/portfolio/PortfolioPage/tabs/target-allocation/types';

import { AddConvictionDialog } from './AddConvictionDialog';
import { formatConvictionValue, getConvictionTypeDisplay } from './convictionUtils';

interface ConvictionsCardProps {
    accounts: Account[];
    convictions: Record<string, ExtendedConviction[]>;
    onUpdate?: (hasUnsavedChanges: boolean) => void;
}

export const ConvictionsCard = ({ accounts, convictions, onUpdate }: ConvictionsCardProps) => {
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);
    const { mutate: updateAccountConvictions } = useUpdateAccountConvictions(accountGroupGUID);
    const { isEditing, setEditingCard } = useEditableCardGroup();
    const cardIsEditing = isEditing('target-allocation', 'convictions');

    // Local state for editing
    const [editingConvictions, setEditingConvictions] = useState<Record<string, ExtendedConviction[]>>({});

    // Use editing state when in edit mode, otherwise use props
    const displayConvictions = cardIsEditing ? editingConvictions : convictions;
    const accountEntries = Object.entries(displayConvictions);

    // Track the expanded accordion item so the Add dialog can default to it
    const [expandedAccount, setExpandedAccount] = useState<string | undefined>(undefined);

    // The currently-expanded account (falling back to the first), shared by the
    // accordion and the Add dialog default so they stay in lockstep.
    const activeAccount = expandedAccount ?? accountEntries[0]?.[0];

    // Check if there are unsaved changes
    const hasChanges = cardIsEditing && JSON.stringify(editingConvictions) !== JSON.stringify(convictions);

    useEffect(() => {
        // Only report unsaved changes when actively editing AND there are changes
        onUpdate?.(hasChanges);
    }, [hasChanges, onUpdate]);

    const handleEdit = () => {
        // Deep clone the convictions for editing
        setEditingConvictions(cloneDeep(convictions));
    };

    const handleAddConviction = (conviction: Conviction, accountGUID: string, asset: AssetInfo) => {
        if (!asset.scId) {
            toast.error(`${asset.symbol ?? asset.sId} has no share class id and cannot be added as a conviction`);
            return;
        }

        // Find the account name for the new conviction
        const account = accounts.find((acc) => acc.accountGUID === accountGUID);

        // Create the new extended conviction
        const newConviction: ExtendedConviction = {
            ...conviction,
            securityId: asset.sId,
            shareClassId: asset.scId,
            account: account?.accountName.plainText || 'Unknown Account',
            ticker: asset.symbol || 'Unknown Ticker',
        };

        // Update the editing state
        setEditingConvictions((prev) => {
            const updated = { ...prev };
            if (updated[accountGUID]) {
                updated[accountGUID] = [...updated[accountGUID], newConviction];
            } else {
                updated[accountGUID] = [newConviction];
            }
            return updated;
        });
    };

    const handleSave = () => {
        // Get all accounts that originally had convictions (including those now empty)
        const allAccountIds = new Set([...Object.keys(convictions), ...Object.keys(editingConvictions)]);

        // Convert editing state to save format, ensuring empty arrays for accounts with no convictions
        const convictionsToSave: AccountConvictionsSaveRequest[] = Array.from(allAccountIds).map((accountId) => {
            const accountConvictions =
                editingConvictions[accountId]?.map((accountConviction) =>
                    omit(accountConviction, 'account', 'ticker', '__type'),
                ) || []; // Provide empty array if no convictions

            return {
                accountGUID: accountId,
                convictions: accountConvictions,
            };
        });

        updateAccountConvictions(
            {
                convictions: convictionsToSave,
            },
            {
                onSuccess: () => {
                    setEditingCard('target-allocation', null);
                    setEditingConvictions({}); // Reset editing state
                },
            },
        );
    };

    const handleCancel = () => {
        // Reset editing state
        setEditingConvictions({});
    };

    const handleRemoveConviction = (accountGUID: string, convictionIndex: number) => {
        setEditingConvictions((prev) => {
            const updated = { ...prev };
            if (updated[accountGUID]) {
                updated[accountGUID] = updated[accountGUID].filter((_, index) => index !== convictionIndex);
                // Remove the account entry if no convictions left
                if (updated[accountGUID].length === 0) {
                    delete updated[accountGUID];
                }
            }
            return updated;
        });
    };
    return (
        <EditableCard
            title="Convictions"
            headerClassName={cardIsEditing ? undefined : 'bg-gray-200'}
            icon={LuAward}
            editGroup="target-allocation"
            editCardId="convictions"
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
        >
            {isEmpty(displayConvictions) ? (
                <div className="text-muted-foreground text-center">No convictions</div>
            ) : (
                <Accordion
                    type="single"
                    collapsible
                    value={activeAccount}
                    onValueChange={setExpandedAccount}
                    className="w-full"
                >
                    {accountEntries.map(([accountId, accountConvictions]) => (
                        <AccordionItem key={accountId} value={accountId} className="">
                            <AccordionTrigger className="font-halyard rounded-none bg-gray-50 px-4 py-3 text-sm hover:bg-gray-100 data-[state=open]:bg-gray-100">
                                <div className="flex w-full items-start justify-between pr-2">
                                    <div className="flex flex-col items-start gap-1">
                                        <div className="font-semibold text-gray-800">
                                            {displayConvictions[accountId][0]?.account}
                                        </div>
                                        <div className="text-xs font-normal text-gray-600">
                                            {accountConvictions.length} conviction
                                            {accountConvictions.length !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <div className="space-y-3 p-5">
                                    <div className="mr-3 flex items-center justify-between border-b px-3 pb-2">
                                        <span className="text-sm font-medium text-gray-600">Ticker</span>
                                        <span className="w-24 text-right text-sm font-medium text-gray-600">
                                            Target
                                        </span>
                                    </div>
                                    {accountConvictions.map((conviction: ExtendedConviction, index: number) => (
                                        <div
                                            key={conviction.securityId}
                                            className="relative mr-3 flex items-center justify-between rounded-md p-3"
                                        >
                                            <span className="font-halyard font-medium">
                                                {conviction.ticker || 'N/A'}
                                            </span>
                                            <div className="flex flex-col items-end text-right">
                                                <span className="font-halyard font-semibold">
                                                    {formatConvictionValue(conviction)}
                                                </span>
                                                <span className="text-xs text-gray-600">
                                                    {getConvictionTypeDisplay(conviction.convictionType)}
                                                </span>
                                                {conviction.convictionType === ConvictionType.WEIGHT && (
                                                    <span className="text-xs text-gray-500 italic">
                                                        (of Portfolio NAV)
                                                    </span>
                                                )}
                                            </div>
                                            {cardIsEditing && (
                                                <button
                                                    onClick={() => handleRemoveConviction(accountId, index)}
                                                    className="absolute top-1/2 right-[-30px] -translate-y-1/2 rounded-full p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                                                >
                                                    <LuX className="h-4 w-4" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            )}
            {cardIsEditing && (
                <div className="flex justify-center pt-4">
                    <AddConvictionDialog
                        accounts={accounts}
                        existingConvictions={displayConvictions}
                        defaultAccountGUID={activeAccount}
                        onAdd={handleAddConviction}
                    />
                </div>
            )}
        </EditableCard>
    );
};
